import React, { PureComponent } from 'react';
import { LoadClassification, MonitoredLoadValue, TimePeriod } from 'app/model/CORE';
import TimeUtils from '../../../../../common/util/TimeUtils';
import { CurrentTimeLine } from './CurrentTimeLine';
import { Flex } from '../../../../common/Flex';
import MonitoredLoadUtils from '../../../../../common/util/MonitoredLoadUtils';
import { SvgUtils } from 'app/common/util/SvgUtils';
import TimePeriodUtils from '../../../../../common/util/TimePeriodUtils';
import Utils from 'app/common/util/Utils';

export interface MonitoredLoadBarProps {
    loadValues: Array<MonitoredLoadValue>;
    monitoredPeriod: TimePeriod;
    activePeriods?: Array<TimePeriod>;
    showLoadInactivePeriods?: boolean;
    currentTime?: Date;
    barHeightEm: number;
    marginTopEm?: number;
    marginBottomEm?: number;
    label?: string;
    labelTitle?: string;
    hideLabel?: boolean;
    drawBottomLine?: boolean;
    fillColor: (loadClassification: LoadClassification, nonRelevant?: boolean) => string;
    occupancyPeriods?: TimePeriod[];
    defaultSelectedPeriod?: TimePeriod;
    selectedPeriod?: TimePeriod;
    canSelectPeriod?: boolean;
    onSelectedPeriodChange?(selectedPeriod: TimePeriod): void;
    title?: string;
}

interface MonitoredLoadBarState {
    selectedPeriod?: TimePeriod;
}

export class MonitoredLoadBar extends PureComponent<MonitoredLoadBarProps, MonitoredLoadBarState> {
    private svgRef = React.createRef<SVGSVGElement>();
    private clicked: string;

    constructor(props) {
        super(props);
        this.state = {
            selectedPeriod: props.defaultSelectedPeriod,
        };
    }

    componentDidUpdate(prevProps: MonitoredLoadBarProps, prevState): void {
        if (this.props.defaultSelectedPeriod !== prevProps.defaultSelectedPeriod) {
            this.setState({ selectedPeriod: this.props.defaultSelectedPeriod });
        }
    }

    getMouseEventTime = e => {
        const svgPt = SvgUtils.getSvgCoordinates(this.svgRef?.current, e);
        const svgWidth = SvgUtils.getSvgWidth(this.svgRef?.current);
        const millisFromStart = (svgPt.x / svgWidth) * TimePeriodUtils.getDurationInMs(this.props.monitoredPeriod);

        return TimeUtils.withoutSeconds(TimeUtils.addMillis(this.props.monitoredPeriod.from, millisFromStart));
    };

    getSelectedPeriod = () => {
        // Value may come from the props (Controlled) or from the state (Uncontrolled)
        return this.props.selectedPeriod !== undefined ? this.props.selectedPeriod : this.state.selectedPeriod;
    };

    setSelectedPeriod = (selectedPeriod: TimePeriod) => {
        if (this.props.selectedPeriod !== undefined) {
            // Controlled logic
            this.props.onSelectedPeriodChange?.(selectedPeriod);
        } else {
            // UnControlled logic
            this.setState({ selectedPeriod: selectedPeriod }, () =>
                this.props.onSelectedPeriodChange?.(selectedPeriod),
            );
        }
    };

    handleMouseDown = e => {
        // If the click is before the mean between current MAX and MIN,
        // consider that MIN will change with mouse move.
        // If no initial selected period has been provided, define the FROM value
        // and consider any subsequent mouse move has defining the TO value.
        const clickedTime = this.getMouseEventTime(e);
        const selectedPeriod = this.getSelectedPeriod();
        const hasSelectedPeriod = selectedPeriod && TimePeriodUtils.isValid(selectedPeriod);
        if (!hasSelectedPeriod) {
            this.clicked = 'FROM';
        } else if (
            2 * TimeUtils.toTimestamp(clickedTime) <
            TimeUtils.toTimestamp(selectedPeriod.from) + TimeUtils.toTimestamp(selectedPeriod.to)
        ) {
            this.clicked = 'FROM';
        } else {
            this.clicked = 'TO';
        }

        this.handleMouseMove(e);

        if (!hasSelectedPeriod) {
            this.clicked = 'TO';
        }
    };

    handleMouseUp = () => {
        this.clicked = undefined;
    };

    handleMouseMove = e => {
        if (!this.clicked) return undefined;

        const clickedTime = TimeUtils.withoutSeconds(this.getMouseEventTime(e)); // Round to the minute below
        const selectedPeriod = this.getSelectedPeriod();
        if (this.clicked === 'FROM' && clickedTime !== selectedPeriod?.from) {
            this.setSelectedPeriod({ from: clickedTime, to: selectedPeriod?.to });
        } else if (this.clicked === 'TO' && clickedTime !== selectedPeriod?.to) {
            this.setSelectedPeriod({ from: selectedPeriod?.from, to: clickedTime });
        }
    };

    handleMouseOut = () => {
        this.clicked = undefined;
    };

    renderMonitoredLoadValue = (monitoredLoadValue: MonitoredLoadValue, index: number) => {
        const monitoredPeriod = this.props.monitoredPeriod;
        const x =
            (100 * TimeUtils.diffInMs(monitoredLoadValue.timePeriod.from.getTime(), monitoredPeriod.from.getTime())) /
            TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
        const width =
            (100 *
                TimeUtils.diffInMs(
                    monitoredLoadValue.timePeriod.to.getTime(),
                    monitoredLoadValue.timePeriod.from.getTime(),
                )) /
            TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
        const fillColor = this.props.fillColor(monitoredLoadValue.loadClassification, monitoredLoadValue.nonRelevant);
        const marginTopEm = this.props.marginTopEm || 0;

        return (
            <rect
                key={'mv-' + index}
                x={x.toFixed(5) + '%'}
                y={`${marginTopEm}em`}
                width={width.toFixed(5) + '%'}
                height={this.props.barHeightEm + 'em'}
                fill={fillColor}
            />
        );
    };

    renderInactivePeriods = () => {
        if (this.props.showLoadInactivePeriods || !this.props.activePeriods) return undefined;

        // This is used to draw a rect over the inactive periods,
        // as a result the load is hidden for inactive periods
        const monitoredPeriod = this.props.monitoredPeriod;
        const marginTopEm = this.props.marginTopEm || 0;
        return MonitoredLoadUtils.getInactivePeriods(monitoredPeriod, this.props.activePeriods).map(
            (inactivePeriod, index) => {
                const x =
                    (100 * TimeUtils.diffInMs(inactivePeriod.from.getTime(), monitoredPeriod.from.getTime())) /
                    TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
                const width =
                    (100 * TimeUtils.diffInMs(inactivePeriod.to.getTime(), inactivePeriod.from.getTime())) /
                    TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
                const fillColor = this.props.fillColor(LoadClassification.UNDEFINED, true);

                return (
                    <rect
                        key={'ip-' + index}
                        x={x.toFixed(5) + '%'}
                        y={`${marginTopEm}em`}
                        width={width.toFixed(5) + '%'}
                        height={this.props.barHeightEm + 'em'}
                        fill={fillColor}
                    />
                );
            },
        );
    };

    renderActivationBottomLine = (bottomLineHeightEm: number) => {
        if (!this.props.drawBottomLine || !this.props.activePeriods) return undefined;

        // The logic below is to draw a dashed line for inactive periods, and to draw a solid line for active periods.
        const monitoredPeriod = this.props.monitoredPeriod;
        const marginTopEm = this.props.marginTopEm || 0;
        const result = [];

        // Compute inactive period from the list of active periods
        MonitoredLoadUtils.getInactivePeriods(monitoredPeriod, this.props.activePeriods).forEach(
            (inactivePeriod, index) => {
                const x =
                    (100 * TimeUtils.diffInMs(inactivePeriod.from.getTime(), monitoredPeriod.from.getTime())) /
                    TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
                const width =
                    (100 * TimeUtils.diffInMs(inactivePeriod.to.getTime(), inactivePeriod.from.getTime())) /
                    TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());

                result.push(
                    <line
                        key={'inactive-period-' + index}
                        x1={x.toFixed(5) + '%'}
                        y1={`${marginTopEm + this.props.barHeightEm}em`}
                        x2={(x + width).toFixed(5) + '%'}
                        y2={`${marginTopEm + this.props.barHeightEm}em`}
                        stroke="#FFFFFF"
                        strokeWidth={bottomLineHeightEm + 'em'}
                        strokeDasharray={bottomLineHeightEm * 4 + 'em ' + bottomLineHeightEm * 4 + 'em'}
                        shapeRendering="geometricPrecision"
                    />,
                );
            },
        );

        this.props.activePeriods.forEach((activePeriod, index) => {
            const x =
                (100 * TimeUtils.diffInMs(activePeriod.from.getTime(), monitoredPeriod.from.getTime())) /
                TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
            const width =
                (100 * TimeUtils.diffInMs(activePeriod.to.getTime(), activePeriod.from.getTime())) /
                TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());

            result.push(
                <line
                    key={'active-period-' + index}
                    x1={x.toFixed(5) + '%'}
                    y1={`${marginTopEm + this.props.barHeightEm}em`}
                    x2={(x + width).toFixed(5) + '%'}
                    y2={`${marginTopEm + this.props.barHeightEm}em`}
                    stroke="#FFFFFF"
                    strokeWidth={bottomLineHeightEm + 'em'}
                    shapeRendering="geometricPrecision"
                />,
            );
        });
        return result;
    };

    renderSelectedOrOccupancyPeriods = () => {
        if (this.getSelectedPeriod()) return this.renderPeriods([this.getSelectedPeriod()]);

        return this.renderPeriods(this.props.occupancyPeriods);
    };

    renderPeriods = (periods: TimePeriod[]) => {
        if (!periods) return undefined;

        const monitoredPeriod = this.props.monitoredPeriod;
        return periods.map((period, index) => {
            const result = [];
            let x;
            if (period.from) {
                x =
                    (100 * TimeUtils.diffInMs(period.from.getTime(), monitoredPeriod.from.getTime())) /
                    TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
                result.push(
                    <line
                        key={'monitored-period-start-' + index}
                        x1={x.toFixed(5) + '%'}
                        y1={0}
                        x2={x.toFixed(5) + '%'}
                        y2={'100%'}
                        stroke="rgb(96, 177, 204)"
                        strokeWidth={'1px'}
                        shapeRendering="geometricPrecision"
                    />,
                );

                if (period.to) {
                    const width =
                        (100 * TimeUtils.diffInMs(period.to.getTime(), period.from.getTime())) /
                        TimeUtils.diffInMs(monitoredPeriod.to.getTime(), monitoredPeriod.from.getTime());
                    result.push(
                        <rect
                            key={'monitored-period-' + index}
                            x={x.toFixed(5) + '%'}
                            y="0"
                            width={width.toFixed(5) + '%'}
                            height="100%"
                            fill={'rgba(96, 177, 204, 0.4)'}
                        />,
                        <line
                            key={'monitored-period-end-' + index}
                            x1={(x + width).toFixed(5) + '%'}
                            y1={0}
                            x2={(x + width).toFixed(5) + '%'}
                            y2={'100%'}
                            stroke="rgb(96, 177, 204)"
                            strokeWidth={'1px'}
                            shapeRendering="geometricPrecision"
                        />,
                    );
                }
            }

            return <g key={'occupancy-period-' + index}>{result}</g>;
        });
    };
    renderLabel = () => {
        if (this.props.hideLabel !== null && this.props.hideLabel) return undefined;

        const marginTopEm = this.props.marginTopEm || 0;
        const marginBottomEm = this.props.marginBottomEm || 0;
        return (
            <div
                title={this.props.labelTitle}
                style={{
                    color: '#A3A3A3',
                    fontWeight: 'bold',
                    fontSize: '0.85em',
                    lineHeight: this.props.barHeightEm,
                    alignSelf: 'flex-end',
                    width: '1em',
                    marginTop: `${marginTopEm}em`,
                    marginBottom: `${marginBottomEm}em`,
                }}>
                {this.props.label}
            </div>
        );
    };

    /**
     * Transparent SVG elements that are used to detect when mouse is over a time slide, or leaves the graph zone.
     */
    renderMouseOverElements = () => {
        if (!this.props.canSelectPeriod || !this.props.monitoredPeriod) return null;

        return (
            <g>
                <rect
                    key={`time-slice-all`}
                    className={`time-slice-all`}
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="transparent"
                    onMouseOut={this.handleMouseOut}
                    onMouseMove={this.handleMouseMove}
                    onMouseDown={this.handleMouseDown}
                    onMouseUp={this.handleMouseUp}
                    onClick={e =>
                        e.stopPropagation()
                    } /* Prevent click in this area to trigger any action in parent component */
                />
            </g>
        );
    };

    render() {
        // Only display non-empty load values, and load values that actually intersects with the time period
        // In case of measures, some loads may be outside the monitoredPeriod and thus and empty line was drawn
        if (
            Utils.isEmpty(this.props.loadValues) ||
            !this.props.monitoredPeriod ||
            !this.props.loadValues.some(lv => TimePeriodUtils.intersects(lv.timePeriod, this.props.monitoredPeriod))
        )
            return null;

        const bottomLineHeightEm = 0.05;
        const marginTopEm = this.props.marginTopEm || 0;
        const marginBottomEm = this.props.marginBottomEm || 0;
        const heightEm = this.props.barHeightEm + marginTopEm + marginBottomEm + bottomLineHeightEm;

        return (
            <Flex size={heightEm + 'em'} style={{ marginBottom: '0.3em' }} title={this.props.title}>
                {this.renderLabel()}

                <svg ref={this.svgRef} height={heightEm + 'em'} style={{ width: '100%', shapeRendering: 'crispEdges' }}>
                    {/* load bar */}
                    {this.props.loadValues.map((monitoredLoadValue, index) => {
                        return this.renderMonitoredLoadValue(monitoredLoadValue, index);
                    })}
                    {/* Inactive periods */}
                    {this.renderInactivePeriods()}

                    {/* Bottom line */}
                    {this.renderActivationBottomLine(bottomLineHeightEm)}

                    {/* Occupancy periods of selected flights or selected period*/}
                    {this.renderSelectedOrOccupancyPeriods()}

                    {/* Current time */}
                    {this.props.currentTime && (
                        <CurrentTimeLine
                            monitoredPeriod={this.props.monitoredPeriod}
                            currentTime={this.props.currentTime}
                        />
                    )}

                    {this.renderMouseOverElements()}
                </svg>
            </Flex>
        );
    }
}
